import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Flame, Crown, Utensils, Apple, Dumbbell, Medal, Scale, Award, Star, Lock, ChevronRight, Trophy,
} from 'lucide-react';

const ICONS = {
  sparkles: Sparkles, flame: Flame, crown: Crown, utensils: Utensils, apple: Apple,
  dumbbell: Dumbbell, medal: Medal, scale: Scale, award: Award, star: Star,
};

const BadgeTile = ({ b }) => {
  const Icon = ICONS[b.icon] || Award;
  return (
    <div
      data-testid={`badge-${b.key}`}
      className={`relative rounded-2xl border p-4 flex flex-col items-center text-center transition-colors ${
        b.earned ? 'border-primary/40 bg-primary/[0.07]' : 'border-white/8 bg-white/[0.02]'
      }`}
    >
      <div
        className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
          b.earned ? 'bg-primary/20 text-primary' : 'bg-zinc-800/80 text-zinc-600'
        }`}
      >
        {b.earned ? <Icon className="w-6 h-6" /> : <Lock className="w-5 h-5" />}
      </div>
      <p className={`font-secondary font-bold text-sm leading-tight ${b.earned ? 'text-white' : 'text-zinc-400'}`}>
        {b.title}
      </p>
      <p className="text-[11px] text-zinc-500 leading-snug mt-1">{b.desc}</p>
      {!b.earned && (
        <div className="w-full mt-3">
          <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full bg-zinc-500 rounded-full transition-all duration-700" style={{ width: `${b.pct}%` }} />
          </div>
          <p className="text-[10px] text-zinc-600 mt-1 tabular-nums">{b.value}/{b.target}</p>
        </div>
      )}
    </div>
  );
};

/**
 * BadgesSection — shows earned + locked badges.
 *   compact: a slim dashboard strip (earned icons + count) that links to /journey.
 *   full:    the complete grid for the Journey page.
 */
const BadgesSection = ({ badges, earnedCount = 0, totalCount = 0, compact = false }) => {
  const navigate = useNavigate();
  if (!badges || badges.length === 0) return null;

  if (compact) {
    const earned = badges.filter((b) => b.earned).slice(0, 5);
    const show = earned.length ? earned : badges.slice(0, 5);
    return (
      <button
        type="button"
        onClick={() => navigate('/journey')}
        data-testid="badges-strip"
        className="hpme-card w-full p-4 mt-3 lg:mt-5 flex items-center gap-3 text-left hover:border-white/25 active:scale-[0.99] transition-colors"
      >
        <Trophy className="w-4 h-4 text-primary shrink-0" />
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          {show.map((b) => {
            const Icon = ICONS[b.icon] || Award;
            return (
              <span
                key={b.key}
                className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                  b.earned ? 'bg-primary/20 text-primary' : 'bg-zinc-800 text-zinc-600'
                }`}
              >
                {b.earned ? <Icon className="w-4 h-4" /> : <Lock className="w-3.5 h-3.5" />}
              </span>
            );
          })}
          <span className="text-xs text-zinc-400 ml-1.5 truncate">
            <span className="text-white font-bold tabular-nums">{earnedCount}</span>/{totalCount} badges earned
          </span>
        </div>
        <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />
      </button>
    );
  }

  return (
    <div className="hpme-card p-6 overflow-hidden" data-testid="badges-section">
      <div className="hpme-card-corner" />
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-secondary font-bold text-lg tracking-tight">BADGES &amp; MILESTONES</h3>
        <span className="hpme-number text-lg text-primary tabular-nums" data-testid="badges-count">{earnedCount}/{totalCount}</span>
      </div>
      <p className="text-xs text-zinc-500 mb-4">Earned for showing up — consistency, balance and habits. Locked ones show your progress.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {badges.map((b) => <BadgeTile key={b.key} b={b} />)}
      </div>
    </div>
  );
};

export default BadgesSection;
